import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { format, startOfWeek, endOfWeek } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  ThemeProvider,
  CssBaseline,
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  Modal,
  CircularProgress,
  Divider,
  Paper,
  createTheme,
} from '@mui/material';
import {
  ChevronRight,
  Close,
  AutoAwesome,
} from '@mui/icons-material';
import { bigDataTheme } from './theme';
import Header from './components/Header';

interface Event {
  id: string;
  rp_entity_id: string;
  event_date: string;
  event_type: string;
  company_name: string;
  stock_symbol: string;
  title?: string;
  description?: string;
}

interface Analysis {
  company_name: string;
  event_date: string;
  summary_type: string;
  summary: string;
  generated_at: string;
}

const API_BASE_URL = '/api';

function App() {
  const [events, setEvents] = useState<{ past_events: Event[]; upcoming_events: Event[] }>({
    past_events: [],
    upcoming_events: []
  });
  const [loading, setLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [pastEventsToShow, setPastEventsToShow] = useState<Event[]>([]);
  const [upcomingEventsToShow, setUpcomingEventsToShow] = useState<Event[]>([]);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const EVENTS_PER_PAGE = 10;

  const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(currentWeek, { weekStartsOn: 1 });

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const theme = createTheme({
    ...bigDataTheme,
    palette: {
      ...bigDataTheme.palette,
      mode: isDarkMode ? 'dark' : 'light',
    },
  });

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newWeek = new Date(currentWeek);
    if (direction === 'prev') {
      newWeek.setDate(newWeek.getDate() - 7);
    } else {
      newWeek.setDate(newWeek.getDate() + 7);
    }
    setCurrentWeek(newWeek);
  };

  const fetchEvents = useCallback(async () => {
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }

    fetchTimeoutRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const startDate = format(weekStart, 'yyyy-MM-dd');
        const endDate = format(weekEnd, 'yyyy-MM-dd');
        
        const response = await axios.get(`${API_BASE_URL}/events`, {
          params: { start_date: startDate, end_date: endDate }
        });
        
        setEvents(response.data as { past_events: Event[]; upcoming_events: Event[] });
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setLoading(false);
      }
    }, 300);
  }, [currentWeek]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    setPastEventsToShow(events.past_events.slice(0, EVENTS_PER_PAGE));
    setUpcomingEventsToShow(events.upcoming_events.slice(0, EVENTS_PER_PAGE));
  }, [events]);

  useEffect(() => {
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, []);

  const handleEventClick = async (event: Event) => {
    setSelectedEvent(event);
    setAnalysisLoading(true);
    setAnalysis(null);

    try {
      const response = await axios.post(`${API_BASE_URL}/analysis`, {
        rp_entity_id: event.rp_entity_id,
        company_name: event.company_name,
        event_date: event.event_date,
        event_type: event.event_type
      });
      
      setAnalysis(response.data as Analysis);
    } catch (error) {
      console.error('Error generating analysis:', error);
    } finally {
      setAnalysisLoading(false);
    }
  };

  const closeModal = () => {
    setSelectedEvent(null);
    setAnalysis(null);
    setAnalysisLoading(false);
  };

  const loadMorePastEvents = () => {
    const nextBatch = events.past_events.slice(0, pastEventsToShow.length + EVENTS_PER_PAGE);
    setPastEventsToShow(nextBatch);
  };

  const loadMoreUpcomingEvents = () => {
    const nextBatch = events.upcoming_events.slice(0, upcomingEventsToShow.length + EVENTS_PER_PAGE);
    setUpcomingEventsToShow(nextBatch);
  };

  const hasMorePastEvents = pastEventsToShow.length < events.past_events.length;
  const hasMoreUpcomingEvents = upcomingEventsToShow.length < events.upcoming_events.length;

  const EventCard = ({ event, isPast }: { event: Event; isPast: boolean }) => {
    const getFiscalInfo = () => {
      if (!event.event_type) return 'Unknown';
      const parts = event.event_type.split('-');
      let fiscalPeriod = '';
      let fiscalYear = '';
      
      // Look for fiscal period (Q1, Q2, Q3, Q4, FY)
      for (let i = 1; i < parts.length; i++) {
        if (parts[i].match(/^Q[1-4]$|^FY$/)) {
          fiscalPeriod = parts[i];
          break;
        }
      }
      
      // Look for fiscal year (4-digit year)
      for (let i = 1; i < parts.length; i++) {
        if (parts[i].match(/^\d{4}$/)) {
          fiscalYear = parts[i];
          break;
        }
      }
      
      if (fiscalPeriod && fiscalYear) {
        return `${fiscalPeriod} ${fiscalYear}`;
      } else if (fiscalPeriod) {
        return fiscalPeriod;
      } else if (fiscalYear) {
        return fiscalYear;
      }
      return 'Unknown';
    };

    return (
      <Card 
        sx={{ 
          cursor: 'pointer', 
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: 3,
          }
        }}
        onClick={() => handleEventClick(event)}
      >
        <CardContent sx={{ p: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box flex={1}>
              <Typography variant="subtitle1" fontWeight={600}>
                {event.company_name || 'Unknown Company'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ({event.stock_symbol || 'N/A'})
              </Typography>
            </Box>
            <Chip 
              label={getFiscalInfo()} 
              size="small" 
              sx={{ mx: 1 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mx: 1 }}>
              {format(new Date(event.event_date), 'MMM dd, HH:mm')}
            </Typography>
            <ChevronRight fontSize="small" />
          </Box>
        </CardContent>
      </Card>
    );
  };

  const LoadingSpinner = () => (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="300px">
      <CircularProgress size={40} />
      <Typography variant="body1" sx={{ mt: 2 }}>
        Loading events...
      </Typography>
    </Box>
  );

  const AnalysisLoadingSpinner = () => (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="150px">
      <CircularProgress size={32} />
      <Typography variant="body1" sx={{ mt: 2 }}>
        Generating AI summary...
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        This may take a few moments
      </Typography>
    </Box>
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Header onToggleTheme={toggleTheme} isDarkMode={isDarkMode} />
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Week Navigation */}
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <Button 
            variant="outlined" 
            onClick={() => navigateWeek('prev')}
            sx={{ color: '#fefefe', borderColor: '#fefefe' }}
          >
            Previous Week
          </Button>
          <Typography variant="h6" sx={{ color: '#fefefe' }}>
            {format(weekStart, 'MMM dd')} - {format(weekEnd, 'MMM dd, yyyy')}
          </Typography>
          <Button 
            variant="outlined" 
            onClick={() => navigateWeek('next')}
            sx={{ color: '#fefefe', borderColor: '#fefefe' }}
          >
            Next Week
          </Button>
        </Box>

        {/* Main Content */}
        {loading ? (
          <LoadingSpinner />
        ) : (
          <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={3}>
            {/* Past Events Column */}
            <Box flex={1}>
              <Paper elevation={2} sx={{ p: 3 }}>
                <Typography variant="h2" component="h2" sx={{ mb: 2 }}>
                  Past Earnings Calls ({events.past_events.length})
                </Typography>
                
                <Box display="flex" flexDirection="column" gap={1}>
                  {pastEventsToShow.map((event) => (
                    <EventCard key={event.id} event={event} isPast={true} />
                  ))}
                  {events.past_events.length === 0 && (
                    <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                      No past earnings calls this week
                    </Typography>
                  )}
                </Box>
                
                {hasMorePastEvents && (
                  <Button 
                    variant="outlined" 
                    fullWidth 
                    onClick={loadMorePastEvents}
                    sx={{ mt: 2, color: '#fefefe', borderColor: '#fefefe' }}
                  >
                    Show More ({events.past_events.length - pastEventsToShow.length} remaining)
                  </Button>
                )}
              </Paper>
            </Box>

            {/* Upcoming Events Column */}
            <Box flex={1}>
              <Paper elevation={2} sx={{ p: 3 }}>
                <Typography variant="h2" component="h2" sx={{ mb: 2 }}>
                  Upcoming Earnings Calls ({events.upcoming_events.length})
                </Typography>
                
                <Box display="flex" flexDirection="column" gap={1}>
                  {upcomingEventsToShow.map((event) => (
                    <EventCard key={event.id} event={event} isPast={false} />
                  ))}
                  {events.upcoming_events.length === 0 && (
                    <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                      No upcoming earnings calls this week
                    </Typography>
                  )}
                </Box>
                
                {hasMoreUpcomingEvents && (
                  <Button 
                    variant="outlined" 
                    fullWidth 
                    onClick={loadMoreUpcomingEvents}
                    sx={{ mt: 2, color: '#fefefe', borderColor: '#fefefe' }}
                  >
                    Show More ({events.upcoming_events.length - upcomingEventsToShow.length} remaining)
                  </Button>
                )}
              </Paper>
            </Box>
          </Box>
        )}

        {/* Event Details Modal */}
        <Modal
          open={!!selectedEvent}
          onClose={closeModal}
          aria-labelledby="event-modal-title"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '90%',
              maxWidth: 800,
              maxHeight: '90vh',
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 24,
              p: 3,
              overflow: 'auto',
            }}
          >
            {selectedEvent && (
              <>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="h3" component="h2" id="event-modal-title">
                    {selectedEvent.company_name || 'Unknown Company'} ({selectedEvent.stock_symbol || 'N/A'})
                  </Typography>
                  <IconButton onClick={closeModal} size="large">
                    <Close />
                  </IconButton>
                </Box>
                
                <Divider sx={{ mb: 3 }} />
                
                {/* Event Info */}
                <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Typography variant="body1">
                      <strong>Time:</strong> {format(new Date(selectedEvent.event_date), 'HH:mm')}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Earnings Call:</strong> {selectedEvent.title || 'Unknown Period'}
                    </Typography>
                  </Box>
                </Paper>

                {/* AI Summary Section */}
                <Paper elevation={1} sx={{ p: 2 }}>
                  <Typography variant="h4" component="h3" display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
                    <AutoAwesome color="primary" />
                    AI Summary
                  </Typography>
                  
                  {analysisLoading ? (
                    <AnalysisLoadingSpinner />
                  ) : analysis ? (
                    <Box>
                      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                        <Chip 
                          label={(analysis.summary_type || 'Summary').replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          color="primary"
                          size="small"
                        />
                        <Typography variant="body2" color="text.secondary">
                          Generated: {format(new Date(analysis.generated_at), 'MMM dd, yyyy HH:mm')}
                        </Typography>
                      </Box>
                      
                      <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.900' }}>
                        <ReactMarkdown 
                          remarkPlugins={[remarkGfm]}
                          components={{
                            h1: ({children}) => <Typography variant="h4" component="h1" sx={{ mb: 1 }}>{children}</Typography>,
                            h2: ({children}) => <Typography variant="h5" component="h2" sx={{ mb: 1 }}>{children}</Typography>,
                            h3: ({children}) => <Typography variant="h6" component="h3" sx={{ mb: 1 }}>{children}</Typography>,
                            p: ({children}) => <Typography variant="body1" sx={{ mb: 1 }}>{children}</Typography>,
                            ul: ({children}) => <Box component="ul" sx={{ pl: 2, mb: 1 }}>{children}</Box>,
                            li: ({children}) => <Typography component="li" variant="body1">{children}</Typography>,
                            strong: ({children}) => <Typography component="strong" fontWeight="bold">{children}</Typography>,
                            em: ({children}) => <Typography component="em" fontStyle="italic">{children}</Typography>,
                            code: ({children}) => <Typography component="code" sx={{ bgcolor: 'grey.800', px: 0.5, borderRadius: 0.5 }}>{children}</Typography>,
                          }}
                        >
                          {analysis.summary}
                        </ReactMarkdown>
                      </Paper>
                    </Box>
                  ) : (
                    <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                      Click an event to generate AI summary.
                    </Typography>
                  )}
                </Paper>
              </>
            )}
          </Box>
        </Modal>
      </Container>
    </ThemeProvider>
  );
}

export default App;